import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { TextColors } from '../../styles/Colors';
import { PopupStyle } from '../../styles/Standard';
import { TabStyles } from '../../styles/TabStyles';
import { isCtrl, isLegacyEdge } from '../../utils/BrowserDetection';
import { makeTestId } from '../../utils/Declarative';
import { generateId } from '../../utils/DomUtil';
import { HorizontalOriginOption, OriginOptions, Overlay, OverlayProps, VerticalOriginOption } from '../../utils/Overlay';
import { FilteringListMessages } from '../List/FilteringList';
import { List, ListIsActiveContext, ListItem } from '../List/List';
import { findSelectableItemIndex, isSelectableItem, standardItemFilter } from '../List/ListItemUtils';
import { TextFieldInputProps, TextFieldInputWidget } from '../TextField/TextFieldInputWidget';

export { ListItem };

export interface ComboboxProps extends
  Pick<OverlayProps, 'getAnchorClientRect' | 'stackingRootId' | 'zIndex'>,
  Pick<TextFieldInputProps, 'aria-label' | 'aria-labelledby' | 'className' | 'disabled' | 'id' | 'inputRef' | 'placeholder'> {

  /**
   * The list of items available for selection from the list popup.
   * Most use cases will want to filter this prop in response to `onFilterItems`.
   */
  listItems: ListItem[];

  /** The maximum height of the list popup. */
  listMaxHeight?: React.CSSProperties['height'];

  /**
   * Invoked when the user has entered text.
   * Most use cases will want to filter `listItems` in response to this event.
   * To allow a user to select a new value (i.e. not otherwise included in the list of options),
   * insert an item with `value: filterText` as the first item in `listItems`.
   */
  onFilterItems?: (filterText: string) => void;

  /** Invoked when the user presses ENTER or clicks an item in the popup list. */
  onSelectValue?: (value: string) => void;

  testId: string;
}

export interface ComboboxState {
  inputText: string;
  isOpen: boolean;
  selectedItemIndex: number;
}

const TextFieldStyleName = TypeStyle.style({
  backgroundColor: 'transparent',
  border: 'none',
  color: TextColors.Primary,
  fontSize: TabStyles.Typography.Sizing.Default,
  height: TabStyles.Sizing.RowHeight,
  lineHeight: `${TabStyles.Sizing.RowHeight}px`,
  padding: 0,
});

const PlaceholderStyleName = TypeStyle.style({
  color: TextColors.Neutral,
  lineHeight: `${TabStyles.Sizing.RowHeight}px`,
  pointerEvents: 'none',
  position: 'absolute',
  $nest: {
    '[aria-disabled="true"] &': { color: TextColors.Disabled },
  }
});

const DefaultState: ComboboxState = Object.freeze({ inputText: '', isOpen: false, selectedItemIndex: -1 });
const OverlayPosition: OriginOptions = Object.freeze({ horizontal: HorizontalOriginOption.left, vertical: VerticalOriginOption.bottom });

/**
 * Internal implementation of an ARIA-spec combobox for use with the ChipPicker widget.
 * Not exported separately in SharedWidgets.ts as it is not Tableau-styled and has some ChipPicker-specific concerns baked in.
 */
export class Combobox extends React.PureComponent<ComboboxProps, ComboboxState> {
  private listRef: React.RefObject<List> = React.createRef();
  private inputElement: HTMLInputElement | null = null;
  private listId: string = generateId();
  private measureRef: React.RefObject<HTMLSpanElement> = React.createRef();

  public state: ComboboxState = { ...DefaultState };
  public static defaultProps: Pick<ComboboxProps, 'listItems'> = { listItems: [] };

  public componentDidMount(): void {
    this.updateInputWidth();
  }

  public componentDidUpdate(prevProps: ComboboxProps, prevState: ComboboxState): void {
    const { inputText, isOpen, selectedItemIndex } = this.state;

    // close and clear if disabled
    if (!prevProps.disabled && this.props.disabled) {
      this.setState(DefaultState);
      return;
    }

    // reset aria-activedescendant after filtering
    if (isOpen && prevProps.listItems !== this.props.listItems) {
      const firstItem = this.props.listItems[0];
      this.setState({
        selectedItemIndex: isSelectableItem(firstItem) && firstItem.value === inputText ? 0 : -1
      });
    }

    // filter items as the user types
    if (inputText !== prevState.inputText) {
      this.props.onFilterItems?.(inputText);
      // done here rather than `render` because otherwise the measurement will always be one character behind
      this.updateInputWidth();
    }

    if (isOpen && prevState.selectedItemIndex !== selectedItemIndex) {
      this.listRef.current!.setActiveItemIndex(selectedItemIndex);
    }
  }

  // dynamically size the `<input>` to its contents; this is ChipPicker-specific but messy to pull it out
  private updateInputWidth(isFocused: boolean = true): void {
    // ios 11 will not show the keyboard if the input has a width of 0
    const minWidth = isFocused ? (isLegacyEdge ? 4 : 2) : 1;
    this.inputElement!.style.width = `${(this.measureRef.current || { scrollWidth: 0 }).scrollWidth + minWidth}px`;
  }

  private handleSelectValue = (value: string) => {
    this.setState(DefaultState, () => this.props.onSelectValue?.(value));
  }

  private handleInputChange = (inputText: string) => {
    this.setState({ inputText, isOpen: this.state.isOpen || !!inputText });
  }

  /** https://www.w3.org/TR/wai-aria-practices-1.1/#combobox */
  private readonly inputKeyDownHandlers: { [key: string]: (e: React.KeyboardEvent) => void | boolean } = Object.freeze({
    'ArrowUp': e => {
      if (this.state.isOpen) {
        if (e.altKey) {
          this.inputKeyDownHandlers.Escape(e);
        } else {
          this.setState({ selectedItemIndex: findSelectableItemIndex(this.props.listItems, this.state.selectedItemIndex, -1) });
        }
      }
    },
    'ArrowDown': e => {
      if (!this.state.isOpen) {
        this.setState({ isOpen: true });
      }
      if (!e.altKey) {
        this.setState({ selectedItemIndex: findSelectableItemIndex(this.props.listItems, this.state.selectedItemIndex, +1) });
      }
    },
    'Enter': () => {
      if (this.state.isOpen && this.state.selectedItemIndex >= 0) {
        const activeItem = this.props.listItems[this.state.selectedItemIndex];
        if (isSelectableItem(activeItem)) {
          this.handleSelectValue(activeItem.value!);
        }
      }
    },
    'Escape': e => {
      if (this.state.isOpen) {
        if (!e.repeat) {
          this.inputKeyUpHandlers[e.key] = () => this.setState({ isOpen: false, selectedItemIndex: -1 }, () => this.inputElement!.focus());
        }
        return false;
      }
      return true;
    },
    'Ctrl+Home': () => {
      if (this.state.isOpen) {
        this.setState({ selectedItemIndex: findSelectableItemIndex(this.props.listItems, -1, 1) });
      }
    },
    'Ctrl+End': () => {
      if (this.state.isOpen) {
        this.setState({ selectedItemIndex: findSelectableItemIndex(this.props.listItems, this.props.listItems.length, -1) });
      }
    }
  });
  private handleInputKeyDown = (e: React.KeyboardEvent<HTMLElement>): void => {
    const key = (isCtrl(e) ? 'Ctrl+' : '') + e.key;
    if (this.inputKeyDownHandlers[key]) {
      if (!this.inputKeyDownHandlers[key](e)) { // handler returning falsy means the event was handled
        e.stopPropagation();
        e.preventDefault();
      }
    } else if (this.state.selectedItemIndex >= 0 && e.which >= 32) { // 32 is SPACE
      // resets aria-activedescendant
      this.setState({ selectedItemIndex: -1 });
    }
  }

  /** Useful for responding to full down-then-up key presses, rather than just down. */
  private readonly inputKeyUpHandlers: { [key: string]: (e: React.KeyboardEvent) => void } = {};
  private handleInputKeyUp = (e: React.KeyboardEvent<HTMLElement>): void => {
    const key = (isCtrl(e) ? 'Ctrl+' : '') + e.key;
    if (this.inputKeyUpHandlers[key]) {
      this.inputKeyUpHandlers[key](e);
      delete this.inputKeyUpHandlers[key];
      e.stopPropagation();
      e.preventDefault();
    }
  }

  private handleInputBlur: React.FocusEventHandler<HTMLInputElement> = e => {
    this.updateInputWidth(false);
    const clickingIntoList = this.state.isOpen && document.getElementById(this.listId)?.contains(e.relatedTarget as HTMLElement);
    if (!clickingIntoList) {
      this.setState(DefaultState);
    }
  }

  private handleInputFocus: React.FocusEventHandler<HTMLInputElement> = () => {
    this.updateInputWidth();
  }

  private handleInputRef = (ref: HTMLInputElement | null): void => {
    this.inputElement = ref;
    this.props.inputRef?.(ref);
  }

  private handlePortalBlur = () => this.setState({ isOpen: false, selectedItemIndex: -1 });

  private handlePortalKey = (e: React.KeyboardEvent<HTMLElement>): void => {
    // allow Escape events from the popup to close the popup
    if (e.key === 'Escape') { e.type === 'keydown' ? this.handleInputKeyDown(e) : this.handleInputKeyUp(e); }
  }
  private portalEventTrapOverrides: OverlayProps['portalEventTrapOverrides'] =
    { onKeyDown: this.handlePortalKey, onKeyUp: this.handlePortalKey, onBlur: this.handlePortalBlur };

  private getAnchorClientRect = (): ClientRect | undefined => {
    if (this.props.getAnchorClientRect) {
      return this.props.getAnchorClientRect();
    } else {
      return this.inputElement!.getBoundingClientRect();
    }
  }

  // fake a placeholder with a positioned div because the text field is usually too narrow to fit any real placeholder text
  private renderPlaceholderText = (placeholder: string) =>
    <span aria-hidden={true} className={PlaceholderStyleName}>{placeholder}</span>

  // hidden div used to measure the input field text, used to resize the field as the user types
  private renderMeasurementDiv = (inputText: string) =>
    <span ref={this.measureRef} style={{ whiteSpace: 'pre', position: 'absolute', visibility: 'hidden' }}>{inputText}</span>

  public render(): JSX.Element {
    // eslint-disable-next-line deprecation/deprecation
    const { className, disabled, listItems, id, listMaxHeight, placeholder, stackingRootId, testId, zIndex } = this.props;
    const { inputText, isOpen, selectedItemIndex } = this.state;

    const activeItemId = (isOpen && selectedItemIndex >= 0) ? `${this.listId}-${selectedItemIndex}` : undefined;
    const renderItems = listItems.length === 0 ?
      [{ value: FilteringListMessages.noMatches(), disabled: true }] :
      standardItemFilter(listItems, ''); // filter out e.g. consecutive separators

    // follows the ARIA 1.0 Combobox design pattern; the ARIA 1.1 pattern is now considered deprecated
    // see: https://www.levelaccess.com/differences-aria-1-0-1-1-changes-rolecombobox/
    return <>
      {/* eslint-disable-next-line jsx-a11y/role-supports-aria-props */}
      <TextFieldInputWidget
        aria-activedescendant={activeItemId}
        aria-autocomplete='list'
        aria-controls={isOpen ? this.listId : undefined}
        aria-expanded={isOpen}
        aria-haspopup='listbox'
        aria-label={this.props['aria-label']}
        aria-labelledby={this.props['aria-labelledby']}
        aria-placeholder={placeholder}
        className={TypeStyle.classes(TextFieldStyleName, className)}
        disabled={disabled}
        handleChange={this.handleInputChange}
        id={id}
        inputRef={this.handleInputRef}
        onBlur={this.handleInputBlur}
        onFocus={this.handleInputFocus}
        onKeyDown={this.handleInputKeyDown}
        onKeyUp={this.handleInputKeyUp}
        role='combobox'
        testId={testId}
        value={inputText}
      />
      {inputText ? this.renderMeasurementDiv(inputText) : (placeholder && this.renderPlaceholderText(placeholder))}
      {!disabled && isOpen &&
        <Overlay
          allowAutoPosition
          anchorOrigin={OverlayPosition}
          getAnchorClientRect={this.getAnchorClientRect}
          portalEventTrapOverrides={this.portalEventTrapOverrides}
          stackingRootId={stackingRootId}
          zIndex={zIndex}
        >
          <ListIsActiveContext.Provider value={!!activeItemId}>
            <List
              id={this.listId}
              items={renderItems}
              onSelect={this.handleSelectValue}
              ref={this.listRef}
              selectOnEnterKey
              style={{ ...PopupStyle, maxHeight: listMaxHeight, minWidth: this.getAnchorClientRect()?.width }}
              tabIndex={-1}
              testId={makeTestId(testId, 'List')}
            />
          </ListIsActiveContext.Provider>
        </Overlay>
      }
    </>;
  }
}
